import { RecordType, SETTING_DEFAULTS } from '@wiztime/core';
import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLive } from '../../../hooks/useLive';
import { useStore } from '../../../hooks/useServices';
import { useSettingsState } from '../../../hooks/useSettings';
import { readStorage, writeStorage } from '../../../lib/safeStorage';
import { FLAG_ON, StorageKey } from '../../../lib/storageKeys';
import styles from './Cards.module.css';

/** Touch devices get the long-press wording (ui-redesign §3.6.4). */
function isTouchOnly(): boolean {
  try {
    return window.matchMedia('(hover: none)').matches;
  } catch {
    return false;
  }
}

/** The default duration in the long written form of the locale: "2 小时", "1 小时 30 分钟", "45 分钟". */
function describeDuration(t: TFunction, totalMinutes: number): string {
  const whole = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(whole / 60);
  const minutes = whole % 60;
  if (hours === 0) return t('firstUse.durationMinutes', { minutes });
  if (minutes === 0) return t('firstUse.durationHours', { hours });
  return t('firstUse.durationHoursMinutes', { hours, minutes });
}

/**
 * One line above the timeline while this device has no block at all: it says how to record. It
 * ends for good (flag in localStorage) once a block exists, however it got there, or when closed.
 */
export function FirstUseHint() {
  const { t } = useTranslation();
  const store = useStore();
  const settings = useSettingsState();
  const [done, setDone] = useState(() => readStorage(StorageKey.RecordHintDone) === FLAG_ON);
  const anyBlock = useLive(
    async () => done || (await store.repo.listByType(RecordType.TimeBlock)).length > 0,
    [store, done],
  );

  // A block exists: the hint is over for good (it is already hidden by the check below).
  const hasBlock = anyBlock.data === true;
  const [sawBlock, setSawBlock] = useState(false);
  if (hasBlock && !sawBlock) setSawBlock(true); // clearing that block later must not bring the hint back
  useEffect(() => {
    if (hasBlock) writeStorage(StorageKey.RecordHintDone, FLAG_ON);
  }, [hasBlock]);

  if (done || sawBlock || anyBlock.data !== false) return null;
  // "2 小时" follows the default-duration setting (ui-redesign §3.6.4).
  const duration = describeDuration(t, (settings.data ?? SETTING_DEFAULTS).defaultDurationMin);
  return (
    <div className={styles.firstUse} data-testid="first-use-hint">
      <span>{isTouchOnly() ? t('firstUse.hintTouch') : t('firstUse.hintMouse', { duration })}</span>
      <button
        type="button"
        className={styles.firstUseClose}
        aria-label={t('firstUse.dismiss')}
        onClick={() => {
          writeStorage(StorageKey.RecordHintDone, FLAG_ON);
          setDone(true);
        }}
      >
        ×
      </button>
    </div>
  );
}
